import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

type AuthContextType = {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  useEffect(() => {
    // Initialize auth session with error handling for invalid tokens
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing auth...');
        
        // First, check if there are auth tokens in the URL (for password reset, etc.)
        const handleAuthFromUrl = async () => {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const searchParams = new URLSearchParams(window.location.search);
          
          // Check hash first (Supabase's preferred method)
          let accessToken = hashParams.get('access_token');
          let refreshToken = hashParams.get('refresh_token');
          let type = hashParams.get('type');
          
          // Fallback to search params if hash is empty
          if (!accessToken) {
            accessToken = searchParams.get('access_token');
            refreshToken = searchParams.get('refresh_token');
            type = searchParams.get('type');
          }
          
          // If we have tokens from URL and it's a password recovery, redirect to reset page
          if (accessToken && (type === 'recovery' || type === 'password_recovery')) {
            console.log('Found password recovery tokens in URL');
            
            // If we're not already on the reset password page, redirect there
            if (!window.location.pathname.includes('/reset-password')) {
              console.log('Redirecting to reset password page...');
              // Preserve the tokens in the URL for the reset password page to handle
              const resetUrl = `/reset-password${window.location.hash || window.location.search}`;
              window.location.replace(resetUrl);
              return null;
            }
            
            // If we're already on the reset password page, let that page handle the tokens
            return null;
          }
          
          return null;
        };
        
        // Try to handle auth from URL first
        const urlSession = await handleAuthFromUrl();
        
        // If URL handling didn't redirect us, continue with normal session check
        if (urlSession === null && !window.location.pathname.includes('/reset-password')) {
          console.log('📡 Getting session...');
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            // If there's an error getting the session (like invalid refresh token),
            // clear the session and start fresh
            console.log('❌ Session error, clearing:', error.message);
            
            // Clear local storage to remove invalid tokens
            try {
              localStorage.removeItem('supabase.auth.token');
              // Clear all Supabase auth keys
              Object.keys(localStorage).forEach(key => {
                if (key.startsWith('sb-') && key.includes('-auth-token')) {
                  localStorage.removeItem(key);
                }
              });
            } catch (storageError) {
              console.log('Storage clear error (non-critical):', storageError);
            }
            
            await supabase.auth.signOut({ scope: 'local' });
            setUser(null);
            setIsAdmin(false);
            setLoading(false);
            return;
          }
          
          console.log('👤 Session user:', session?.user?.email || 'No user');
          setUser(session?.user ?? null);
          
          if (session?.user) {
            // Don't wait for admin check - set loading to false immediately
            setLoading(false);
            // Check admin status in background
            checkAdminStatus(session.user.id);
          } else {
            console.log('✅ Auth initialized - no user');
            setLoading(false);
          }
        }
      } catch (error: any) {
        console.error('💥 Auth initialization error:', error);
        
        // Clear any invalid session data from localStorage
        try {
          localStorage.removeItem('supabase.auth.token');
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') && key.includes('-auth-token')) {
              localStorage.removeItem(key);
            }
          });
        } catch (storageError) {
          console.log('Storage clear error (non-critical):', storageError);
        }
        
        // Clear any invalid session data
        await supabase.auth.signOut({ scope: 'local' });
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
      }
    };

    initializeAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Auth state change:', event, session?.user?.email || 'No user');
      
      // Handle token refresh errors
      if (event === 'TOKEN_REFRESHED') {
        console.log('✅ Token refreshed successfully');
      }
      
      // Handle sign out or token expired
      if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        // Check admin status in background without blocking
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      console.log('🔍 Checking admin status for:', userId);
      
      // Reduced timeout to 3 seconds for faster response
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Database connection timeout - proceeding with default permissions')), 3000)
      );
      
      const adminCheckPromise = supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single()
        .then(result => {
          if (result.error) {
            console.log('📊 Database query error:', result.error.message);
            return { data: null, error: result.error };
          }
          return result;
        });
      
      let result;
      try {
        result = await Promise.race([adminCheckPromise, timeoutPromise]);
      } catch (timeoutError: any) {
        console.warn('⏰ Admin check timed out, defaulting to user role:', timeoutError.message);
        setIsAdmin(false);
        return; // Exit early on timeout
      }

      const { data, error } = result as any;

      if (error) {
        console.log('🔧 Handling admin check error:', error.code, error.message);
        
        // If profile doesn't exist, create it
        if (error.code === 'PGRST116') {
          console.log('📝 Profile not found, creating...');
          try {
            const createResult = await Promise.race([
              supabase
                .from('profiles')
                .insert({
                  id: userId,
                  username: `user_${userId.slice(0, 8)}`,
                  ratings_count: 0,
                  role: 'user',
                }),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Profile creation timeout')), 3000)
              )
            ]);
            
            const { error: insertError } = createResult as any;
            if (insertError) {
              console.error('❌ Error creating profile:', insertError);
            } else {
              console.log('✅ Profile created successfully');
            }
          } catch (createError: any) {
            console.warn('⏰ Profile creation timed out or failed:', createError.message);
          }
        }
        
        setIsAdmin(false);
      } else {
        console.log('✅ Admin status:', data?.role || 'user');
        setIsAdmin(data?.role === 'admin');
      }
    } catch (error: any) {
      console.warn('💡 Admin check encountered an issue, using safe defaults:', error.message);
      
      // Always default to non-admin on any error
      setIsAdmin(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error: any) {
      // If it's a refresh token error, clear storage and retry
      if (error.message?.includes('Refresh Token') || error.message?.includes('Invalid Refresh Token')) {
        console.log('Clearing invalid tokens and retrying...');
        try {
          localStorage.removeItem('supabase.auth.token');
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') && key.includes('-auth-token')) {
              localStorage.removeItem(key);
            }
          });
        } catch (storageError) {
          console.log('Storage clear error (non-critical):', storageError);
        }
        await supabase.auth.signOut({ scope: 'local' });
      }
      throw error;
    }
  };

  const signUp = async (email: string, password: string, username: string) => {
    if (isSigningUp) return;
    
    try {
      setIsSigningUp(true);
      
      // Sign up the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: username,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create user');

      // Get the Supabase URL from environment
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      // Create profile using Edge Function with improved retry logic
      const functionUrl = `${supabaseUrl}/functions/v1/create-profile`;
      
      let retries = 8; // Reduced from 15
      let lastError = null;

      // Shorter initial wait
      console.log('Waiting for auth system to settle...');
      await new Promise(resolve => setTimeout(resolve, 1500)); // Reduced from 3000

      while (retries > 0) {
        try {
          console.log(`Attempting to create profile (attempt ${9 - retries}/8)...`);
          
          const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({
              userId: authData.user.id,
              username: username,
              email: email,
            }),
          });

          const responseText = await response.text();
          console.log('Edge Function response:', response.status, responseText);

          if (!response.ok) {
            lastError = responseText;
            
            try {
              const errorData = JSON.parse(responseText);
              
              // Check for auth user not found or foreign key constraint errors
              if (errorData.code === 'AUTH_USER_NOT_FOUND' || errorData.code === '23503') {
                console.log('Auth user not ready yet, retrying...');
                retries--;
                if (retries > 0) {
                  // Shorter exponential backoff
                  const waitTime = Math.min(1000 + (9 - retries) * 500, 4000); // Reduced wait times
                  console.log(`Waiting ${waitTime}ms before retry...`);
                  await new Promise(resolve => setTimeout(resolve, waitTime));
                  continue;
                }
              }
              
              // For duplicate key error, consider it success
              if (errorData.code === '23505') {
                console.log('Profile already exists, considering as success');
                return;
              }
            } catch (e) {
              // If we can't parse the error, retry with shorter wait
              retries--;
              if (retries > 0) {
                const waitTime = Math.min(1000 + (9 - retries) * 500, 4000);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
              }
            }
            
            throw new Error(`Profile creation failed: ${responseText}`);
          }

          // Success! Parse and return
          const result = JSON.parse(responseText);
          
          if (result.error && (result.code === 'AUTH_USER_NOT_FOUND' || result.code === '23503')) {
            console.log('Auth user not ready yet, retrying...');
            retries--;
            if (retries > 0) {
              const waitTime = Math.min(1000 + (9 - retries) * 500, 4000);
              await new Promise(resolve => setTimeout(resolve, waitTime));
              continue;
            }
          }

          if (result.error) {
            throw new Error(`Profile creation error: ${result.error}`);
          }

          console.log('Profile created successfully:', result);
          return;
          
        } catch (error: any) {
          lastError = error.message;
          
          // If it's an AUTH_USER_NOT_FOUND or foreign key error, retry with shorter wait
          if (error.message.includes('AUTH_USER_NOT_FOUND') || error.message.includes('23503')) {
            retries--;
            if (retries > 0) {
              console.log(`Retrying... (${retries} attempts left)`);
              const waitTime = Math.min(1000 + (9 - retries) * 500, 4000);
              await new Promise(resolve => setTimeout(resolve, waitTime));
              continue;
            }
          }
          
          throw error;
        }
      }

      // All retries exhausted - but don't throw error, just proceed
      console.warn('Profile creation attempts exhausted, proceeding with signup completion');
      
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    } finally {
      setIsSigningUp(false);
    }
  };

  const signOut = async () => {
    try {
      // Check if there's an active session before attempting to sign out
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // No active session, just clear local state
        setUser(null);
        setIsAdmin(false);
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        // Ignore AuthSessionMissingError as it means user is already signed out
        if (error.message?.includes('Auth session missing')) {
          console.log('Session already cleared');
          setUser(null);
          setIsAdmin(false);
          return;
        }
        throw error;
      }
      setIsAdmin(false);
    } catch (error: any) {
      console.error('Sign out error:', error);
      // Ignore AuthSessionMissingError as it means user is already signed out
      if (error.message?.includes('Auth session missing') || error.name === 'AuthSessionMissingError') {
        console.log('Session already cleared');
      }
      // Clear local state even if sign out fails
      setUser(null);
      setIsAdmin(false);
    } finally {
      // Always clear localStorage on sign out
      try {
        localStorage.removeItem('supabase.auth.token');
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') && key.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
      } catch (storageError) {
        console.log('Storage clear error (non-critical):', storageError);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
